"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { Link } from "@/i18n/routing";
import {
  authors,
  brandLogos,
  menuColumns,
  type BrandLogo,
  type MenuGroup,
} from "@/fixtures/shared/nav/more-menu-fixtures";

type MoreMenuPanelProps = {
  open: boolean;
  /** When true (sticky nav active), dock the panel to the viewport top below
   *  the 56px sticky bar instead of absolutely under the in-flow nav. */
  pinned?: boolean;
  onClose: () => void;
};

const STICKY_BAR_HEIGHT = 56;
const TRANSITION_MS = 200;

export function MoreMenuPanel({
  open,
  pinned = false,
  onClose,
}: MoreMenuPanelProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  /**
   * QA: "smooth animation on transition in and out of the navbar mega menu."
   *
   * The panel used to `return null` when closed, so both directions were an
   * instant mount/unmount with nothing to animate. It now stays mounted for the
   * length of the exit: `mounted` controls the DOM, `shown` controls the
   * transition, and `shown` is only raised on the frame AFTER mounting so the
   * browser has a "from" state to animate out of.
   */
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const id = setTimeout(() => setMounted(false), TRANSITION_MS);
    return () => clearTimeout(id);
  }, [open]);

  if (!mounted) return null;

  return (
    <>
      <button
        aria-label="關閉選單"
        onClick={onClose}
        tabIndex={-1}
        className={`fixed inset-0 z-40 hidden cursor-default transition-opacity duration-200 ease-out motion-reduce:transition-none lg:block ${
          shown ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        role="dialog"
        aria-label="更多選單"
        className={`z-50 hidden w-full origin-top transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none lg:block ${
          pinned ? "fixed left-0 right-0" : "absolute left-0 right-0 top-full"
        } ${shown ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"}`}
        style={{
          backgroundColor: "#E5E5E5",
          borderBottom: "1px solid #333",
          ...(pinned
            ? {
                top: STICKY_BAR_HEIGHT,
                maxHeight: `calc(100vh - ${STICKY_BAR_HEIGHT}px)`,
                overflowY: "auto",
              }
            : null),
        }}
      >
        <div
          className="mx-auto flex w-full max-w-[1440px] px-4 lg:px-[48px]"
          style={{ paddingTop: 32, paddingBottom: 48, gap: 48 }}
        >
          <div
            className="grid min-w-0 flex-1 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
            style={{ columnGap: 32, rowGap: 32 }}
          >
            {menuColumns.map((col, i) => (
              <div key={i} className="flex flex-col" style={{ gap: 24 }}>
                {col.map((group) => (
                  <MenuColumnGroup key={group.heading} group={group} />
                ))}
              </div>
            ))}
          </div>
          <aside
            className="hidden shrink-0 flex-col lg:flex"
            style={{ width: 350, gap: 48 }}
          >
            <BrandLogosSection />
            <AuthorsSection />
          </aside>
        </div>
      </div>
    </>
  );
}

function MenuColumnGroup({ group }: { group: MenuGroup }) {
  return (
    <div className="flex flex-col" style={{ gap: 16 }}>
      <h3
        className="font-bold"
        style={{
          color: "#096344",
          fontSize: 16,
          lineHeight: "20px",
        }}
      >
        {group.heading}
      </h3>
      <ul className="flex flex-col" style={{ gap: 12 }}>
        {group.links.map((link) => (
          <li key={link.label}>
            <Link
              href={link.href}
              className="block text-[#333] transition-colors hover:text-[#108a60]"
              style={{
                fontSize: 14,
                lineHeight: "16px",
              }}
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BrandLogosSection() {
  return (
    <section className="flex flex-col" style={{ gap: 16 }}>
      <h3
        className="font-bold"
        style={{
          color: "#096344",
          fontSize: 16,
          lineHeight: "20px",
        }}
      >
        信報品牌
      </h3>
      <div className="grid grid-cols-2" style={{ gap: 14 }}>
        {brandLogos.map((logo) => (
          <BrandLogoPill key={logo.alt} logo={logo} />
        ))}
      </div>
    </section>
  );
}

function BrandLogoPill({ logo }: { logo: BrandLogo }) {
  return (
    <Link
      href={logo.href}
      className="inline-flex items-center transition-opacity hover:opacity-70"
      style={{
        // border: "1px solid #666",
        // borderRadius: 3,
        // padding: "8px 16px",
        gap: 6,
      }}
    >
      <span
        className="relative block shrink-0"
        style={{ width: "100%", height: 32 }}
        // style={{ width: logo.width, height: 20 }}
      >
        <Image
          src={logo.src}
          alt={logo.alt}
          fill
          className="object-contain object-left"
          /* The box is the cell, not the mark: a 350px sidebar split into two
             columns with a 14px gutter. `logo.width` describes the mobile pill
             instead, and hinting with it here would ask for an image roughly a
             third of the width this box actually renders at. */
          sizes="168px"
        />
      </span>
    </Link>
  );
}

function AuthorsSection() {
  return (
    <section className="flex flex-col" style={{ gap: 16 }}>
      <h3
        className="font-bold"
        style={{
          color: "#096344",
          fontSize: 16,
          lineHeight: "20px",
        }}
      >
        專欄作家
      </h3>
      <div className="grid grid-cols-2" style={{ columnGap: 16, rowGap: 14 }}>
        {authors.map((a) => (
          <Link
            key={a.name}
            href={a.href}
            className="group flex items-center"
            style={{ gap: 12 }}
          >
            <span
              className="relative block shrink-0 overflow-hidden rounded-full"
              style={{
                width: 40,
                height: 40,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <Image
                src={a.avatar}
                alt={a.name}
                fill
                className="object-cover"
                sizes="40px"
              />
            </span>
            <div className="flex min-w-0 flex-col" style={{ gap: 2 }}>
              <span
                className="truncate text-black transition-colors group-hover:text-[#108a60]"
                style={{ fontSize: 15, lineHeight: "normal" }}
              >
                {a.name}
              </span>
              {a.column && (
                <span
                  className="truncate"
                  style={{
                    color: "#6f6f6f",
                    fontSize: 13,
                    lineHeight: "normal",
                  }}
                >
                  {a.column}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
